import moment from 'moment';
import { getJwtToken } from './auth';

export const DPR_REPORT_STATUS = {
  NOT_REQUESTED: 1,
  REQUESTED: 2,
  ERROR: 3,
  GENERATED: 4,
};

/** UI modal values → API reportType */
export const mapUiReportTypeToApi = (uiType) => {
  const map = {
    pdf: 'welding',
    onepage: 'onePage',
    combined: 'combined',
  };
  return map[uiType] || null;
};

/** UI delivery mode → API output */
export const mapUiOutputToApi = (deliveryMode) => (
  deliveryMode === 'shareConfigured' ? 'email' : 'download'
);

const parseApiJson = async (response) => {
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(json.error || json.message || 'Report request failed');
    err.status = response.status;
    throw err;
  }
  return json.data ?? json;
};

export const generateDprReport = async ({
  dprLogId,
  reportType,
  output,
  ongoingPipelineId,
  dprProjectStatus,
}) => {
  const authToken = await getJwtToken();
  const body = {
    dprLogId: Number(dprLogId),
    reportType,
    output,
  };
  if (ongoingPipelineId != null && ongoingPipelineId !== '') {
    body.ongoingPipelineId = Number(ongoingPipelineId);
  }
  if (dprProjectStatus) {
    body.dprProjectStatus = dprProjectStatus;
  }
  const response = await fetch(`${process.env.REACT_APP_BASE_URL}/dpr-log/generate-report`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: authToken,
    },
    body: JSON.stringify(body),
  });
  return parseApiJson(response);
};

export const refreshReportDownloadUrl = async (dprLogId, reportType) => {
  const authToken = await getJwtToken();
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/dpr-log/${dprLogId}/report-progress?reportType=${encodeURIComponent(reportType)}`,
    {
      headers: {
        Authorization: authToken,
      },
    }
  );
  return parseApiJson(response);
};

export const generateFlashReport = async ({
  dprLogId,
  shift,
  emailToDestinations,
  ongoingPipelineId,
  dprProjectStatus,
}) => {
  const authToken = await getJwtToken();
  let url = `${process.env.REACT_APP_BASE_URL}/dpr-log/generate-flash-report?dprLogId=${dprLogId}&shift=${encodeURIComponent(shift)}`;
  if (ongoingPipelineId != null && ongoingPipelineId !== '') {
    url += `&ongoingPipelineId=${encodeURIComponent(ongoingPipelineId)}`;
  }
  if (dprProjectStatus) {
    url += `&dprProjectStatus=${encodeURIComponent(dprProjectStatus)}`;
  }
  if (emailToDestinations?.length) {
    url += `&emailToDestinations=${encodeURIComponent(emailToDestinations.join(','))}`;
  }
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: authToken,
    },
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || 'Failed to generate flash report');
  }
  return response.json().catch(() => null);
};

const pickValidTimestamp = (value) => {
  if (value == null || value === '') return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.toISOString() : null;
};

/** Resolve generated timestamp from API/DB shapes (camelCase, snake_case, legacy). */
export const extractReportGeneratedAt = (source) => {
  if (!source || typeof source !== 'object') return null;
  const candidates = [
    source.generatedAt,
    source.reportGeneratedTime,
    source.generated_at,
    source.report_generated_time,
    source.updatedAt,
    source.createdAt,
    source.timestamp,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const normalized = pickValidTimestamp(candidates[i]);
    if (normalized) return normalized;
  }
  return null;
};

const normalizeStoredReportEntry = (entry, reportDetails) => {
  if (!entry) return null;
  const generatedAt = extractReportGeneratedAt(entry)
    || extractReportGeneratedAt(reportDetails)
    || extractReportGeneratedAt(reportDetails?.original);
  return generatedAt ? { ...entry, generatedAt } : entry;
};

export const formatDprReportGeneratedLabel = (entry) => {
  const generatedAt = extractReportGeneratedAt(entry);
  if (!generatedAt) return 'A report is already saved for this type.';
  return `Last generated ${moment(generatedAt).format('MMM D, YYYY h:mm A')}.`;
};

/** Read stored report entry from GET /dpr-log reportDetails (overwrite model — one slot per type). */
export const getStoredDprReportEntry = (reportDetails, uiReportType) => {
  if (!reportDetails || uiReportType === 'flash') return null;
  const apiType = mapUiReportTypeToApi(uiReportType);
  if (!apiType) return null;

  const fromReports = reportDetails.reports?.[apiType];
  if (fromReports) return normalizeStoredReportEntry(fromReports, reportDetails);

  if (apiType === 'welding' && reportDetails.original?.s3Obj) {
    return normalizeStoredReportEntry({
      reportStatus: DPR_REPORT_STATUS.GENERATED,
      s3Key: reportDetails.original.s3Obj.key,
      fileUrl: reportDetails.original.s3Obj.fileUrl,
      generatedAt: reportDetails.original.generatedAt || null,
      emailSent: false,
    }, reportDetails);
  }
  return null;
};

export const isReportReadyForDownload = (entry) => (
  entry?.reportStatus === DPR_REPORT_STATUS.GENERATED && Boolean(entry?.fileUrl)
);

export const hasGeneratedStoredReport = (entry) => (
  entry?.reportStatus === DPR_REPORT_STATUS.GENERATED
);

export const buildReportEntryFromGenerateResponse = (data) => ({
  reportStatus: data?.reportStatus ?? DPR_REPORT_STATUS.GENERATED,
  fileUrl: data?.fileUrl || null,
  s3Key: data?.s3Key || null,
  generatedAt: extractReportGeneratedAt(data) || new Date().toISOString(),
  emailSent: Boolean(data?.emailSent),
  fileName: data?.fileName,
});

export const upsertDprReportDetailsEntry = (reportDetails, apiReportType, entry) => {
  const base = reportDetails && typeof reportDetails === 'object' ? reportDetails : {};
  return {
    ...base,
    reports: {
      ...(base.reports || {}),
      [apiReportType]: entry,
    },
  };
};

export const ONE_PAGE_UI_REPORT_TYPE = 'onepage';
export const ONE_PAGE_API_REPORT_TYPE = 'onePage';

const resolveStoredOnePageEntry = async ({ dprLogId, reportDetails }) => {
  let entry = getStoredDprReportEntry(reportDetails, ONE_PAGE_UI_REPORT_TYPE);
  if (!hasGeneratedStoredReport(entry) || extractReportGeneratedAt(entry)) {
    return entry;
  }
  try {
    const data = await refreshReportDownloadUrl(dprLogId, ONE_PAGE_API_REPORT_TYPE);
    if (!data) return entry;
    return { ...entry, ...buildReportEntryFromGenerateResponse(data) };
  } catch {
    return entry;
  }
};

/** Open saved one-page DPR or generate and open — no submit/email required. */
export const openOrGenerateOnePageDpr = async ({
  dprLogId,
  reportDetails,
  preferRegenerate = false,
  ongoingPipelineId,
  dprProjectStatus,
  requireOngoingPipeline = false,
}) => {
  if (!dprLogId) {
    throw new Error('DPR log is not loaded yet.');
  }
  if (requireOngoingPipeline && (ongoingPipelineId == null || ongoingPipelineId === '')) {
    throw new Error(
      'Select an ongoing pipeline before generating a report. Set status to Production in the DPR header to choose a pipeline, then save the DPR.'
    );
  }

  if (!preferRegenerate) {
    const storedEntry = await resolveStoredOnePageEntry({ dprLogId, reportDetails });
    if (isReportReadyForDownload(storedEntry)) {
      return {
        fileUrl: storedEntry.fileUrl,
        reportEntry: storedEntry,
        reportDetails: upsertDprReportDetailsEntry(
          reportDetails,
          ONE_PAGE_API_REPORT_TYPE,
          storedEntry
        ),
        openedFromCache: true,
      };
    }
    if (storedEntry?.reportStatus === DPR_REPORT_STATUS.ERROR) {
      throw new Error('Last one-page DPR generation failed. Please try again.');
    }
  }

  const data = await generateDprReport({
    dprLogId,
    reportType: ONE_PAGE_API_REPORT_TYPE,
    output: 'download',
    ongoingPipelineId,
    dprProjectStatus,
  });
  const reportEntry = buildReportEntryFromGenerateResponse(data);
  const nextReportDetails = upsertDprReportDetailsEntry(
    reportDetails,
    ONE_PAGE_API_REPORT_TYPE,
    reportEntry
  );

  if (!data?.fileUrl) {
    return {
      fileUrl: null,
      reportEntry,
      reportDetails: nextReportDetails,
      openedFromCache: false,
    };
  }

  return {
    fileUrl: data.fileUrl,
    reportEntry,
    reportDetails: nextReportDetails,
    openedFromCache: false,
  };
};
